import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Workflow as WorkflowIcon, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function Workflows() {
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/workflows").then((r) => setWorkflows(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Supprimer définitivement ce workflow ?")) return;
    try {
      await api.delete(`/workflows/${id}`);
      toast.success("Workflow supprimé");
      load();
    } catch {
      toast.error("Suppression échouée");
    }
  };

  return (
    <div className="p-8 max-w-7xl mx-auto" data-testid="workflows-page">
      <div className="flex items-start justify-between mb-10">
        <div>
          <span className="overline">Workflows</span>
          <h1 className="font-display text-4xl font-medium tracking-tighter mt-2">Vos automatisations</h1>
          <p className="text-zinc-600 mt-2">Créez, activez, surveillez.</p>
        </div>
        <Link to="/app/workflows/new">
          <Button className="rounded-sm bg-black text-white hover:bg-zinc-800" data-testid="workflows-new-btn">
            <Plus size={16} className="mr-2"/> Nouveau workflow
          </Button>
        </Link>
      </div>

      {loading ? (
        <div className="text-sm text-zinc-500 p-12 text-center">Chargement...</div>
      ) : workflows.length === 0 ? (
        <div className="bg-white border border-zinc-200 rounded-sm p-16 text-center">
          <WorkflowIcon size={40} strokeWidth={1.5} className="mx-auto text-zinc-300 mb-4"/>
          <p className="font-display text-xl font-medium tracking-tight mb-2">Rien à afficher ici.</p>
          <p className="text-sm text-zinc-500 mb-6">Lancez votre première automatisation.</p>
          <Link to="/app/workflows/new">
            <Button className="rounded-sm bg-black text-white hover:bg-zinc-800">
              <Plus size={16} className="mr-2"/> Créer un workflow
            </Button>
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {workflows.map((w) => (
            <div key={w.id} className="bg-white border border-zinc-200 rounded-sm p-6 hover-lift" data-testid={`workflow-card-${w.id}`}>
              <div className="flex items-start justify-between mb-4">
                <div className="w-10 h-10 rounded-sm bg-zinc-100 flex items-center justify-center">
                  <WorkflowIcon size={18} strokeWidth={1.5}/>
                </div>
                <Badge className={w.active ? "klein-bg text-white rounded-sm font-mono text-[10px]" : "bg-zinc-200 text-zinc-700 rounded-sm font-mono text-[10px]"}>
                  {w.active ? "ACTIF" : "BROUILLON"}
                </Badge>
              </div>
              <h3 className="font-display text-lg font-medium tracking-tight mb-1 line-clamp-2">{w.name}</h3>
              <p className="text-xs text-zinc-500 mb-4 line-clamp-2 min-h-[32px]">{w.description || "Pas de description"}</p>
              <div className="flex items-center justify-between text-xs font-mono text-zinc-500 mb-4">
                <span>{w.steps?.length || 0} étapes</span>
                <span>{w.execution_count || 0} exécutions</span>
                <span>{w.estimated_time_saved_minutes} min saved</span>
              </div>
              <div className="flex items-center gap-2">
                <Link to={`/app/workflows/${w.id}`} className="flex-1">
                  <Button variant="outline" className="w-full rounded-sm border-zinc-300" data-testid={`workflow-view-${w.id}`}>
                    Ouvrir
                  </Button>
                </Link>
                <Button
                  variant="outline"
                  onClick={() => remove(w.id)}
                  className="rounded-sm border-zinc-300 text-red-600 hover:bg-red-50"
                  data-testid={`workflow-delete-${w.id}`}
                >
                  <Trash2 size={14}/>
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
